// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Functional core for CO-5 outward-arc routing. In the loop-aware CLD, a causal
// link should arc *outside* its loop -- bowing away from the ring interior --
// so the curve clears the node text instead of cutting a straight chord across
// the ring (see the 260803 loop-rendering reference: "Links arc outside;
// rounded"). Given a link's endpoints and the centroid of the loop it belongs
// to, this computes the arcPoint the shared Connector geometry curves through.
// Pure + canvas-free, so it unit-tests directly.

import { Point } from './drawing/common';

export interface OutwardArcOptions {
  /** Bulge as a fraction of the chord length. */
  readonly bulgeRatio?: number;
  /** Floor / ceiling on the bulge so tiny and huge links stay reasonable. */
  readonly minBulge?: number;
  readonly maxBulge?: number;
}

const DEFAULT_BULGE_RATIO = 0.22;
const DEFAULT_MIN_BULGE = 16;
const DEFAULT_MAX_BULGE = 70;

/**
 * The point a link should arc through to bow outward from its loop.
 *
 * Returns the chord midpoint pushed away from `centroid` (the loop's center) by
 * a chord-proportional bulge. Returns `undefined` when the outward direction is
 * degenerate -- most importantly a 2-member loop, whose centroid sits exactly on
 * the chord, so there is no "outside" to bow toward; the caller then draws the
 * link straight and lets the loop-cycle lens supply the curve.
 */
export function outwardArcPoint(
  from: Point,
  to: Point,
  centroid: Point,
  options: OutwardArcOptions = {},
): Point | undefined {
  const bulgeRatio = options.bulgeRatio ?? DEFAULT_BULGE_RATIO;
  const minBulge = options.minBulge ?? DEFAULT_MIN_BULGE;
  const maxBulge = options.maxBulge ?? DEFAULT_MAX_BULGE;

  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  const chordLen = Math.hypot(to.x - from.x, to.y - from.y);
  if (chordLen < 1e-9) {
    return undefined;
  }

  let ox = midX - centroid.x;
  let oy = midY - centroid.y;
  const olen = Math.hypot(ox, oy);
  // Centroid on (or almost on) the chord -> no meaningful outward direction.
  if (olen < chordLen * 0.02) {
    return undefined;
  }
  ox /= olen;
  oy /= olen;

  const bulge = Math.min(maxBulge, Math.max(minBulge, chordLen * bulgeRatio));
  return { x: midX + ox * bulge, y: midY + oy * bulge };
}

/**
 * The point a CLD loop link should arc through so its curve follows the loop's
 * RING -- the dynamic tangent-attachment model (Williams 2026-08-11 reference
 * `260811_SA-loop-rendering-reference`), which replaces the fixed compass-port
 * bulge above.
 *
 * With both endpoints already sitting on the loop ring, returning the ring point
 * at their angular bisector makes the three points (`from`, this, `to`)
 * co-circular -- so the shared Connector arc-fit (`circleFromPoints`) reproduces
 * the ring circle exactly and the link becomes a true ring arc. Consecutive links
 * around the loop then share one circle and meet each member at matching slope,
 * so the arcs join into one continuous curve with no above/below-the-word jog.
 *
 * Returns `undefined` when the bisector is degenerate -- an endpoint on the ring
 * center, or the two endpoints diametrically opposite (a 2-member loop, whose
 * edge subtends pi and has no unique short bisector) -- so the caller can fall
 * back to `outwardArcPoint` / a straight link.
 */
/**
 * The point a link should arc through to bow to one side of its chord, forming
 * half of a lens. Used for a 2-member loop (bigon): the two antiparallel links
 * between the same pair must curve to OPPOSITE sides or they collapse onto one
 * line (the "twist" on the Bass floor -- two loops that are each a bigon sharing
 * the Adoption Rate hub). The bow direction is the LEFT perpendicular of
 * `from`->`to`, so the reverse link (`to`->`from`) computes the opposite
 * perpendicular and the two arcs open into a lens automatically -- no side
 * bookkeeping needed. Returns undefined for a degenerate (coincident) chord.
 */
export function perpendicularBowPoint(from: Point, to: Point, options: OutwardArcOptions = {}): Point | undefined {
  const bulgeRatio = options.bulgeRatio ?? 0.3;
  const minBulge = options.minBulge ?? 20;
  const maxBulge = options.maxBulge ?? 90;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) {
    return undefined;
  }
  // Left-perpendicular unit vector of the from->to direction.
  const px = -dy / len;
  const py = dx / len;
  const bulge = Math.min(maxBulge, Math.max(minBulge, len * bulgeRatio));
  return { x: (from.x + to.x) / 2 + px * bulge, y: (from.y + to.y) / 2 + py * bulge };
}

export function ringArcPoint(from: Point, to: Point, center: Point): Point | undefined {
  let ax = from.x - center.x;
  let ay = from.y - center.y;
  let bx = to.x - center.x;
  let by = to.y - center.y;
  const ra = Math.hypot(ax, ay);
  const rb = Math.hypot(bx, by);
  if (ra < 1e-6 || rb < 1e-6) {
    return undefined; // an endpoint sits on the ring center -- no radial direction
  }
  // Unit radial directions to each endpoint.
  ax /= ra;
  ay /= ra;
  bx /= rb;
  by /= rb;
  // Bisector of the two unit directions = the midpoint angle on the SHORT arc (a
  // loop edge of any 3+ member loop subtends < pi, so the short arc is the ring
  // segment between adjacent members).
  let mx = ax + bx;
  let my = ay + by;
  const mlen = Math.hypot(mx, my);
  if (mlen < 1e-6) {
    return undefined; // opposite directions (angle ~ pi): no unique short bisector
  }
  mx /= mlen;
  my /= mlen;
  // Ring radius: average the endpoint radii (equal on a true ring; averaging
  // tolerates members that drifted slightly off it).
  const r = (ra + rb) / 2;
  return { x: center.x + mx * r, y: center.y + my * r };
}
